/**
 * ListViewImportModal — import widoku listy z pliku eksportu (przeniesienie z innego tenanta).
 *
 * Kroki:
 *  1. 'pick'    — wybór pliku JSON (przeciągnij albo „Wybierz plik"); plik czytany w przeglądarce
 *                 i od razu wysyłany do podglądu.
 *  2. 'preview' — raport serwera pogrupowany sekcjami (kolumny, filtry, akcje…): co wejdzie, co z
 *                 ostrzeżeniem, czego nie wczytamy i dlaczego + nazwa nowego widoku. „Podgląd listy"
 *                 pokazuje oczyszczony widok na przykładowych rekordach, „Importuj" zapisuje.
 *
 * Backend: POST /api/system-modules/:slug/layouts/import?dryRun=true (podgląd) | bez dryRun (zapis).
 * Ocena elementów zawsze po stronie serwera (pola tego tenanta) — plik jest niezaufany.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isListActionId,
  LIST_ACTION_CATALOG,
  type ListImportPreviewResponse,
  type ListImportReportEntry,
  type ListImportSection,
} from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Eye, FileJson } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { PositionListPreview } from '@/components/menu/MenuUnifiedPreview';
import { Modal } from '@/components/Modal';
import { PreviewModal } from '@/components/preview/PreviewModal';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import type { KartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import { mapKartotekaFieldsForPreview } from '@/lib/kartoteki/kartoteka-preview-fields';

const P = 'tenant.listBuilder.transfer.import';

/** Kolejność sekcji w raporcie — jak zakładki Kreatora Listy. */
const SECTION_ORDER: ListImportSection[] = [
  'columns',
  'sort',
  'filters',
  'quick_filters',
  'formatting',
  'buttons',
  'actions',
  'widgets',
  'kanban',
  'calendar',
];

const STATUS_TONE = {
  loaded: 'success',
  loaded_with_warning: 'warning',
  skipped: 'danger',
} as const;

export interface ListViewImportModalProps {
  open: boolean;
  onClose: () => void;
  /** Slug kartoteki systemowej, do której importujemy. */
  moduleSlug: string;
  /** Po zapisie — rodzic odświeża układy i przełącza się na nowy widok. */
  onImported: (layoutId: string) => void;
}

type Step = 'pick' | 'preview';

function errorCode(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error ?? null;
  }
  return null;
}

const KNOWN_ERRORS = new Set([
  'INVALID_FORMAT',
  'UNSUPPORTED_FORMAT_VERSION',
  'INVALID_FILE',
  'WRONG_KARTOTEKA',
  'NOTHING_TO_IMPORT',
  'PAYLOAD_TOO_LARGE',
  'FORBIDDEN',
]);

export function ListViewImportModal({
  open,
  onClose,
  moduleSlug,
  onImported,
}: ListViewImportModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [step, setStep] = useState<Step>('pick');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<unknown>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState<ListImportPreviewResponse | null>(null);
  const [name, setName] = useState('');
  // Nazwy zajęte według serwera przy zapisie (ktoś dodał widok w międzyczasie → 409).
  const [lateTakenLower, setLateTakenLower] = useState<string[]>([]);
  const [showListPreview, setShowListPreview] = useState(false);

  // Kolizję nazwy sprawdzamy od razu w oknie (nazwy istniejących widoków przychodzą z podglądu),
  // bez wielkości liter — tak samo jak serwer przy zapisie. Niczego nie dopisujemy sami.
  const takenLower = useMemo(
    () =>
      new Set([
        ...(preview?.existingNames ?? []).map((n) => n.trim().toLowerCase()),
        ...lateTakenLower,
      ]),
    [preview, lateTakenLower],
  );
  const trimmedName = name.trim();
  const nameTaken = trimmedName.length > 0 && takenLower.has(trimmedName.toLowerCase());

  const errorText = (err: unknown): string => {
    const code = errorCode(err);
    return code && KNOWN_ERRORS.has(code) ? t(`${P}.errors.${code}`) : t(`${P}.errors.generic`);
  };

  // Pola kartoteki — do przykładowych rekordów w podglądzie listy (ten sam klucz co kreator menu).
  const definitionQuery = useQuery({
    queryKey: ['tenant', 'kartoteki', moduleSlug, 'definition'],
    queryFn: async () =>
      (await api.get<KartotekaDefinition>(`/kartoteki/${moduleSlug}/definition`)).data,
    enabled: open && step === 'preview',
    staleTime: 5 * 60_000,
    retry: false,
  });
  const previewFields = useMemo(
    () => (definitionQuery.data ? mapKartotekaFieldsForPreview(definitionQuery.data.fields) : []),
    [definitionQuery.data],
  );

  function reset() {
    setStep('pick');
    setFileName(null);
    setFileContent(null);
    setFileError(null);
    setPreview(null);
    setName('');
    setLateTakenLower([]);
    setShowListPreview(false);
  }

  const previewMutation = useMutation({
    mutationFn: async (file: unknown) =>
      (
        await api.post<ListImportPreviewResponse>(
          `/system-modules/${moduleSlug}/layouts/import?dryRun=true`,
          { file },
        )
      ).data,
    onSuccess: (data) => {
      setPreview(data);
      setName(data.proposedName);
      setStep('preview');
    },
    onError: (err) => setFileError(errorText(err)),
  });

  const importMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ layout: { id: string; name: string } }>(
          `/system-modules/${moduleSlug}/layouts/import`,
          { file: fileContent, name: trimmedName },
        )
      ).data,
    onSuccess: (data) => {
      toast.show({ variant: 'success', title: t(`${P}.success`, { name: data.layout.name }) });
      onImported(data.layout.id);
      reset();
      onClose();
    },
    onError: (err) => {
      // Nazwa zajęta w międzyczasie — komunikat pod polem nazwy, nie toast.
      if (errorCode(err) === 'NAME_TAKEN') {
        setLateTakenLower((prev) => [...prev, trimmedName.toLowerCase()]);
        return;
      }
      toast.show({ variant: 'error', title: errorText(err) });
    },
  });

  const running = previewMutation.isPending || importMutation.isPending;

  function handleClose() {
    if (running) return;
    reset();
    onClose();
  }

  async function pickFile(file: File | null) {
    if (!file) return;
    setFileError(null);
    setFileName(file.name);
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setFileError(t(`${P}.errors.notJson`));
      return;
    }
    setFileContent(parsed);
    previewMutation.mutate(parsed);
  }

  // Akcje list raport podaje identyfikatorem z katalogu — pokazujemy ich polską nazwę.
  const entryLabel = (e: ListImportReportEntry): string => {
    if (e.section === 'actions' && isListActionId(e.label)) {
      return t(LIST_ACTION_CATALOG[e.label].labelKey);
    }
    return e.label;
  };

  const bySection = useMemo(() => {
    const map = new Map<ListImportSection, ListImportReportEntry[]>();
    for (const e of preview?.report.entries ?? []) {
      map.set(e.section, [...(map.get(e.section) ?? []), e]);
    }
    return SECTION_ORDER.filter((s) => map.has(s)).map((s) => ({
      section: s,
      entries: map.get(s)!,
    }));
  }, [preview]);

  const footer =
    step === 'preview' ? (
      <>
        {preview?.importable ? (
          <Button
            variant="secondary"
            leftIcon={<Eye className="h-4 w-4" />}
            onClick={() => setShowListPreview(true)}
            disabled={running}
            data-testid="list-import-preview"
          >
            {t(`${P}.previewList`)}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={reset} disabled={running} data-testid="list-import-back">
          {t(`${P}.chooseAnother`)}
        </Button>
        <Button
          variant="primary"
          onClick={() => importMutation.mutate()}
          loading={importMutation.isPending}
          disabled={!preview?.importable || trimmedName.length === 0 || nameTaken || running}
          data-testid="list-import-submit"
        >
          {t(`${P}.submit`)}
        </Button>
      </>
    ) : (
      <Button variant="ghost" onClick={handleClose} disabled={running}>
        {t('common.cancel')}
      </Button>
    );

  return (
    <>
      <Modal
        // Na czas podglądu listy okno importu się chowa (stan zostaje) — inaczej podgląd
        // ląduje POD nim (dwie nakładki naraz) i nie widać go.
        open={open && !showListPreview}
        onClose={handleClose}
        title={t(`${P}.title`)}
        description={t(`${P}.description`)}
        size="lg"
        footer={footer}
        closeOnBackdrop={!running}
        closeOnEsc={!running}
        testId="list-import-modal"
      >
        {step === 'pick' ? (
          <div className="space-y-3" data-testid="list-import-step-pick">
            <div
              onDragOver={(ev) => {
                ev.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(ev) => {
                ev.preventDefault();
                setIsDragOver(false);
                void pickFile(ev.dataTransfer.files?.[0] ?? null);
              }}
              className={[
                'rounded-lg border-2 border-dashed p-8 text-center',
                isDragOver
                  ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                  : 'border-border-secondary',
              ].join(' ')}
              data-testid="list-import-dropzone"
            >
              <FileJson size={36} className="mx-auto text-text-tertiary" aria-hidden="true" />
              <p className="mt-3 text-sm text-text-primary">
                {previewMutation.isPending ? t(`${P}.checking`) : (fileName ?? t(`${P}.dropHint`))}
              </p>
              <label className="mt-3 inline-block cursor-pointer text-sm text-accent-700 hover:underline dark:text-accent-300">
                {/* Brak prymitywu file-input w bibliotece — ukryty natywny input jest tu kanoniczny
                    (ten sam wzorzec co AttachmentsSection i BugReportWidget). */}
                {/* eslint-disable-next-line no-restricted-syntax */}
                <input
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  disabled={running}
                  onChange={(ev) => {
                    void pickFile(ev.target.files?.[0] ?? null);
                    ev.target.value = '';
                  }}
                  data-testid="list-import-file-input"
                />
                {t(`${P}.browse`)}
              </label>
            </div>
            {fileError ? (
              <Alert tone="error" data-testid="list-import-file-error">
                {fileError}
              </Alert>
            ) : null}
          </div>
        ) : preview ? (
          <div className="space-y-4" data-testid="list-import-step-preview">
            <Alert tone="info">{t(`${P}.notTransferredInfo`)}</Alert>

            <Input
              label={t(`${P}.nameLabel`)}
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              error={nameTaken ? t(`${P}.nameTaken`) : undefined}
              data-testid="list-import-name"
            />

            <div className="flex flex-wrap gap-2" data-testid="list-import-counts">
              <Badge tone="success">
                {t(`${P}.counts.loaded`, { n: preview.report.counts.loaded })}
              </Badge>
              {preview.report.counts.loadedWithWarning > 0 ? (
                <Badge tone="warning">
                  {t(`${P}.counts.withWarning`, { n: preview.report.counts.loadedWithWarning })}
                </Badge>
              ) : null}
              <Badge tone={preview.report.counts.skipped > 0 ? 'danger' : 'neutral'}>
                {t(`${P}.counts.skipped`, { n: preview.report.counts.skipped })}
              </Badge>
            </div>

            {!preview.importable ? (
              <Alert tone="warning" data-testid="list-import-nothing">
                {t(`${P}.errors.NOTHING_TO_IMPORT`)}
              </Alert>
            ) : null}

            <div className="divide-y divide-border-secondary rounded-lg border border-border-secondary">
              {bySection.map(({ section, entries }) => (
                <section key={section} data-testid={`list-import-section-${section}`}>
                  <h3 className="bg-bg-secondary px-3 py-1.5 text-xs font-medium text-text-secondary">
                    {t(`${P}.sections.${section}`)}
                  </h3>
                  <ul>
                    {entries.map((e, i) => (
                      <li
                        key={`${section}-${i}`}
                        className="flex items-start gap-3 border-t border-border-secondary px-3 py-2 text-sm first:border-t-0"
                      >
                        <Badge tone={STATUS_TONE[e.status]}>{t(`${P}.status.${e.status}`)}</Badge>
                        <div className="min-w-0 flex-1">
                          <span className="text-text-primary">{entryLabel(e)}</span>
                          {e.reason ? (
                            <p className="text-xs text-text-muted">
                              {t(`${P}.reasons.${e.reason}`)}
                            </p>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>

      {showListPreview && preview ? (
        <PreviewModal
          open
          onClose={() => setShowListPreview(false)}
          title={t(`${P}.previewTitle`, { name: name.trim() || preview.proposedName })}
        >
          <PreviewTenantAuthBoundary>
            <PositionListPreview
              slug={`import-${moduleSlug}`}
              systemKartoteka
              data={{
                entityName: definitionQuery.data?.module.name ?? moduleSlug,
                entitySlug: moduleSlug,
                fields: previewFields,
                formConfig: undefined,
                listConfig: preview.listConfig,
                formViewName: null,
                listViewName: name.trim() || preview.proposedName,
              }}
            />
          </PreviewTenantAuthBoundary>
        </PreviewModal>
      ) : null}
    </>
  );
}
